import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Checkbox } from "../../../components/ui/Checkbox";
import Icon from "../../../components/AppIcon";
import { useAuth } from "../../../context/AuthContext";
import GoogleLoginButton from "../../../components/auth/GoogleLoginButton";

// NEW: OTP UI components and API
import OTPInput from "../../../components/ui/OTPInput";
import CountdownTimer from "../../../components/ui/CountdownTimer";
import { authAPI } from "services/api";

const RegistrationForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  // Steps: 1 = registration form, 2 = OTP verify
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [otpError, setOtpError] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      terms: false,
      newsletter: false,
    },
  });

  const password = watch("password", "");
  const confirmPassword = watch("confirmPassword", "");
  const email = watch("email", "");

  const validatePassword = (value) => {
    if (!value) return "Password is required";
    if (value?.length < 8) return "Password must be at least 8 characters";
    if (!/(?=.*[a-z])/.test(value))
      return "Password must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/.test(value))
      return "Password must contain at least one uppercase letter";
    if (!/(?=.*\d)/.test(value))
      return "Password must contain at least one number";
    return true;
  };

  const validateConfirmPassword = (value) => {
    if (!value) return "Please confirm your password";
    if (value !== password) return "Passwords do not match";
    return true;
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let score = 0;
    if (password?.length >= 8) score++;
    if (/(?=.*[a-z])/.test(password)) score++;
    if (/(?=.*[A-Z])/.test(password)) score++;
    if (/(?=.*\d)/.test(password)) score++;
    if (/(?=.*[@$!%*?&])/.test(password)) score++;

    const strengthMap = {
      0: { strength: 0, label: "", color: "" },
      1: { strength: 20, label: "Very Weak", color: "bg-red-500" },
      2: { strength: 40, label: "Weak", color: "bg-orange-500" },
      3: { strength: 60, label: "Fair", color: "bg-yellow-500" },
      4: { strength: 80, label: "Good", color: "bg-blue-500" },
      5: { strength: 100, label: "Strong", color: "bg-green-500" },
    };

    return strengthMap?.[score];
  };

  const passwordStrength = getPasswordStrength(password);

  // STEP 1: create account request that sends OTP (does NOT login)
  const onSubmitRegistration = async (data) => {
    setIsLoading(true);
    setOtpError("");

    try {
      const result = await authAPI.registerWithOtp({
        name: data.name,
        email: data.email.toLowerCase().trim(),
        password: data.password,
        phone: data.phone || "",
      });

      if (result.success) {
        setCurrentStep(2);
        setTimerActive(true);
      } else {
        if (
          result.message?.includes("already registered") ||
          result.message?.includes("already exists")
        ) {
          setError("email", { type: "manual", message: result.message });
        } else if (result.errors) {
          result.errors.forEach((error) => {
            setError(error.field, {
              type: "manual",
              message: error.message,
            });
          });
        } else {
          setError("root", {
            type: "manual",
            message: result.message || "Registration failed. Please try again.",
          });
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      setError("root", {
        type: "manual",
        message:
          "Registration failed. Please check your connection and try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2: verify OTP, then login + navigate
  const handleVerifyOtp = async (otp) => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP");
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      const result = await authAPI.verifyEmail({
        email: email.toLowerCase().trim(),
        otp,
      });

      if (result.success) {
        login(result.data.user, result.data.token);
        navigate("/dashboard");
      } else {
        setOtpError(result.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setOtpError("OTP verification failed. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setOtpError("");

    try {
      const result = await authAPI.requestVerificationOtp(
        email.toLowerCase().trim()
      );

      if (result.success) {
        setTimerActive(true);
        setOtpError("New OTP sent to your email.");
      } else {
        setOtpError(result.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setOtpError("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimerComplete = () => {
    setTimerActive(false);
    setOtpError("OTP has expired. Please request a new one.");
  };

  // RENDER
  if (currentStep === 2) {
    // OTP step
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
            <Icon name="ShieldCheck" size={32} color="white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Verify Your Email
          </h1>
          <p className="text-muted-foreground">
            We&apos;ve sent a 6-digit code to{" "}
            <span className="font-medium">{email}</span>
          </p>
        </div>

        <div className="space-y-6">
          <OTPInput
            length={6}
            onChange={() => {
              if (otpError) setOtpError("");
            }}
            onComplete={handleVerifyOtp}
            error={otpError}
            isLoading={otpLoading}
          />

          {timerActive && (
            <CountdownTimer
              initialMinutes={15}
              onComplete={handleTimerComplete}
              isRunning={timerActive}
            />
          )}

          {otpError && !timerActive && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={20} className="text-red-600 mr-2" />
                <span className="text-red-700 text-sm">{otpError}</span>
              </div>
            </div>
          )}

          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={handleResendOtp}
            disabled={isLoading || timerActive}
            loading={isLoading}
          >
            {isLoading ? "Sending..." : "Resend OTP"}
          </Button>

          <Button
            variant="ghost"
            size="lg"
            fullWidth
            onClick={() => setCurrentStep(1)}
          >
            Change Email
          </Button>
        </div>
      </div>
    );
  }

  // Registration step
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
          <Icon name="UserPlus" size={32} color="white" />
        </div>
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Create Account
        </h1>
        <p className="text-muted-foreground">
          Join Expense Loop and start managing group expenses
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmitRegistration)} className="space-y-6">
        <Input
          label="Full Name"
          type="text"
          placeholder="Enter your full name"
          error={errors?.name?.message}
          required
          {...register("name", {
            required: "Full name is required",
            minLength: { value: 2, message: "Name must be at least 2 characters" },
            pattern: {
              value: /^[a-zA-Z\s]+$/,
              message: "Name can only contain letters and spaces",
            },
          })}
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="Enter your email"
          error={errors?.email?.message}
          required
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Please enter a valid email address",
            },
          })}
        />

        <Input
          label="Phone Number"
          type="tel"
          placeholder="Enter your phone number (optional)"
          error={errors?.phone?.message}
          {...register("phone", {
            pattern: {
              value: /^[0-9]{10,15}$/,
              message: "Please enter a valid phone number",
            },
          })}
        />

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? "text" : "password"}
            placeholder="Create a strong password"
            error={errors?.password?.message}
            required
            {...register("password", { validate: validatePassword })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-micro"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
          </button>
        </div>

        {password && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Password Strength</span>
              <span
                className={`font-medium ${
                  passwordStrength?.strength >= 80
                    ? "text-green-600"
                    : passwordStrength?.strength >= 60
                    ? "text-blue-600"
                    : passwordStrength?.strength >= 40
                    ? "text-yellow-600"
                    : "text-red-600"
                }`}
              >
                {passwordStrength?.label}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${passwordStrength?.color}`}
                style={{ width: `${passwordStrength?.strength}%` }}
              />
            </div>
          </div>
        )}

        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm your password"
            error={errors?.confirmPassword?.message}
            required
            {...register("confirmPassword", { validate: validateConfirmPassword })}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-micro"
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
          </button>

          {confirmPassword && (
            <div className="flex items-center mt-2 text-sm">
              {confirmPassword === password ? (
                <div className="flex items-center text-green-600">
                  <Icon name="Check" size={16} className="mr-1" />
                  <span>Passwords match</span>
                </div>
              ) : (
                <div className="flex items-center text-red-600">
                  <Icon name="X" size={16} className="mr-1" />
                  <span>Passwords do not match</span>
                </div>
              )}
            </div>
          )}
        </div>

        {errors?.root && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} className="text-red-600 mr-2" />
              <span className="text-red-700 text-sm">{errors?.root?.message}</span>
            </div>
          </div>
        )}

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or continue with
            </span>
          </div>
        </div>

        <GoogleLoginButton
          onGoogleLogin={(user, token) => {
            login(user, token);
            navigate("/dashboard");
          }}
          onError={(errorMessage) => {
            setError("root", {
              type: "manual",
              message: errorMessage || "Google login failed. Please try again.",
            });
          }}
        />

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          iconName="UserPlus"
          iconPosition="left"
          disabled={isLoading}
        >
          {isLoading ? "Creating Account..." : "Create Account"}
        </Button>

        <div className="text-center">
          <p className="text-muted-foreground text-sm">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-primary hover:text-primary/80 font-medium transition-micro"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;
